import { createHash } from 'crypto';
import { isError } from 'remeda';
import { z } from 'zod';

/** Get SSO token */

export type GetSsoToken = ({
  // baseUrl,
  apiBaseUrl,
  product,
  credentials,
}: {
  // baseUrl: string;
  apiBaseUrl?: string;
  product?: string;
  credentials: {
    email: string;
    password: string;
  };
}) => Promise<{ ssoUserId: number; token: string }>;

export const getSsoToken: GetSsoToken = ({
  // baseUrl,
  apiBaseUrl,
  credentials: { email, password },
}) => {
  const normalizedEmail = email.toLowerCase().trim();
  const hashPassword = createHash('md5').update(password).digest('hex');

  if (!apiBaseUrl) {
    return Promise.reject('API base URL is required');
  }

  return fetch(`${apiBaseUrl}/v1/sso/oauth/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      username: normalizedEmail,
      password: hashPassword,
    }),
  })
    .then((res) => res.json())
    .then((json) => NewSsoResponse.parse(json))
    .then((res) => {
      if (isNewSsoSuccess(res)) {
        return {
          token: res.token,
          ssoUserId: res.id,
        };
      }
      return Promise.reject(res);
    })
    .catch((error) => {
      console.error(
        `Failed to get SSO token: ${btoa(email)}`,
        isError(error) ? error.message : JSON.stringify(error),
      );
      const errorMessage = isNewSsoError(error)
        ? error.errorDetails?.message ||
          error.errorReason ||
          'Authentication failed'
        : 'Incorrect e-mail or password';
      return Promise.reject(errorMessage);
    });
};

type NewSsoSuccess = z.infer<typeof NewSsoSuccess>;
const NewSsoSuccess = z.object({
  id: z.number(),
  token: z.string(),
});
function isNewSsoSuccess(res: NewSsoResponse): res is NewSsoSuccess {
  return NewSsoSuccess.safeParse(res).success;
}

type NewSsoError = z.infer<typeof NewSsoError>;
const NewSsoError = z.object({
  errorCode: z.string(),
  errorReason: z.string(),
  errorDetails: z.object({
    message: z.string(),
  }),
  status: z.number(),
  path: z.string(),
});
function isNewSsoError(res: NewSsoResponse): res is NewSsoError {
  return NewSsoError.safeParse(res).success;
}

type NewSsoResponse = z.infer<typeof NewSsoResponse>;
const NewSsoResponse = z.union([NewSsoSuccess, NewSsoError]);

/** Request OTP */

export type RequestOtp = ({
  baseUrl,
  email,
  domain,
}: {
  baseUrl: string;
  email: string;
  domain: string;
}) => Promise<RequestOtpSuccessType>;

const RequestOtpSuccess = z.object({
  data: z.object({
    send: z.literal('success'),
  }),
  status: z.number().optional(),
});

const RequestOtpError = z.object({
  errors: z.object({ send: z.string() }),
  status: z.number().optional(),
});

type RequestOtpSuccessType = z.infer<typeof RequestOtpSuccess>;
type RequestOtpErrorType = z.infer<typeof RequestOtpError>;
type RequestOtpResponseType = RequestOtpSuccessType | RequestOtpErrorType;

function isRequestOtpSuccess(
  res: RequestOtpResponseType,
): res is RequestOtpSuccessType {
  return RequestOtpSuccess.safeParse(res).success;
}
function isRequestOtpError(error: unknown): error is RequestOtpErrorType {
  const parsed = RequestOtpError.safeParse(error);
  return parsed.success;
}
const RequestOtpResponse = z.union([RequestOtpSuccess, RequestOtpError]);

export const requestOtp: RequestOtp = async ({ baseUrl, email, domain }) => {
  const normalizedEmail = email.toLowerCase().trim();
  try {
    const existsEmailResponse = await fetch(`${baseUrl}/api/user/exists`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify({ email: normalizedEmail }),
    });
    const existsData = await existsEmailResponse.json();
    if (existsData.exists === false) {
      return Promise.reject('Unable to request one-time password.');
    }
  } catch (e) {
    console.error(`Failed to check if email exists: ${btoa(email)}`, e);
  }
  await new Promise((resolve) => setTimeout(resolve, 1000));
  return fetch(
    `${baseUrl}/api/otp/send?domain=${encodeURIComponent(
      domain,
    )}&email=${encodeURIComponent(normalizedEmail)}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    },
  )
    .then((res) => res.json())
    .then(RequestOtpResponse.parse)
    .then((res: RequestOtpResponseType) => {
      if (isRequestOtpSuccess(res) && res.data.send === 'success') {
        return res;
      }
      return Promise.reject(res);
    })
    .catch((error) => {
      let errorMessage = 'Unable to request one-time password.';
      if (isRequestOtpError(error)) {
        if (error.status === 400 && error.errors.send === 'failed') {
          errorMessage =
            'Too many OTP requests. Please try again after 30 minutes.';
        } else if (error.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.errors.send;
        }
      }
      console.error(
        `Unable to request one-time password: ${btoa(email)}`,
        isError(error) ? error.message : JSON.stringify(error),
      );
      return Promise.reject(errorMessage);
    });
};

/** Log in with OTP */

export type LogInWithOtp = ({
  baseUrl,
  email,
  otp,
  token,
}: {
  baseUrl: string;
  email: string;
  otp: string;
  token: string;
}) => Promise<{ token: string; ssoUserId: number }>;

const VerifyOtpSuccess = z.object({
  data: z.object({
    access_token: z.string(),
    user_id: z.number().optional(),
    verify: z.string().optional(),
  }),
  status: z.number().optional(),
});

const VerifyOtpError = z.object({
  errors: z.object({ verify: z.string() }),
  status: z.number().optional(),
});

const VerifyOtpFailed = z.object({
  errors: z.object({ verify: z.literal('failed') }),
  status: z.number().optional(),
});

const VerifyOtpSuccessInError = z.object({
  errors: z.object({ verify: z.literal('success') }),
  status: z.number().optional(),
});

type VerifyOtpSuccessType = z.infer<typeof VerifyOtpSuccess>;
type VerifyOtpErrorType = z.infer<typeof VerifyOtpError>;
type VerifyOtpResponseType = VerifyOtpSuccessType | VerifyOtpErrorType;

function isVerifyOtpSuccess(
  res: VerifyOtpResponseType,
): res is VerifyOtpSuccessType {
  const parsed = VerifyOtpSuccess.safeParse(res);
  return parsed.success && !!parsed.data.data.access_token;
}
function isVerifyOtpError(error: unknown): error is VerifyOtpErrorType {
  return VerifyOtpError.safeParse(error).success;
}
function isVerifyOtpFailed(
  error: unknown,
): error is z.infer<typeof VerifyOtpFailed> {
  return VerifyOtpFailed.safeParse(error).success;
}
const VerifyOtpResponse = z.union([
  VerifyOtpSuccess,
  VerifyOtpError,
  VerifyOtpFailed,
  VerifyOtpSuccessInError,
]);

export const logInWithOtp: LogInWithOtp = ({ baseUrl, email, otp, token }) => {
  const normalizedEmail = email.toLowerCase().trim();
  return fetch(
    `${baseUrl}/api/otp/verify?otp=${encodeURIComponent(
      otp,
    )}&email=${encodeURIComponent(normalizedEmail)}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  )
    .then((res) => res.json())
    .then(VerifyOtpResponse.parse)
    .then((res: VerifyOtpResponseType) => {
      if (isVerifyOtpSuccess(res)) {
        return {
          token: res.data.access_token,
          ssoUserId: res.data.user_id ?? 0,
        };
      }
      return Promise.reject(res);
    })
    .catch((error) => {
      let errorMessage = 'Unable to verify one-time password.';
      if (isVerifyOtpError(error)) {
        if (error.status === 400) {
          errorMessage = error.errors.verify.replaceAll(
            'verify',
            'one-time password',
          );
        } else if (error.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = error.errors.verify;
        }
      } else if (isVerifyOtpFailed(error)) {
        errorMessage = 'Invalid one-time password. Please try again.';
      }
      console.error(
        `Unable to verify OTP: ${btoa(email)}`,
        isError(error) ? error.message : JSON.stringify(error),
      );
      return Promise.reject(errorMessage);
    });
};

/** Log in with Google */

export type LogInWithGoogle = ({
  baseUrl,
  product,
  useCustomRlifeSocialAuth,
  accessToken,
}: {
  baseUrl: string;
  product: string;
  useCustomRlifeSocialAuth: boolean;
  accessToken: string;
}) => Promise<{ token: string; ssoUserId: number; isExistingUser: string }>;

export const logInWithGoogle: LogInWithGoogle = ({
  baseUrl,
  product,
  useCustomRlifeSocialAuth,
  accessToken,
}) => {
  if (useCustomRlifeSocialAuth === true) {
    return fetch(`${baseUrl}/api/login/social/google/implicit/store`, {
      method: 'POST',
      body: new URLSearchParams({ access_token: accessToken }),
      headers: {
        accept: 'application/json',
        'X-PRODUCT-REFERER': product,
      },
    })
      .then((res) => res.json())
      .then(GoogleSignInResponse.parse)
      .then((res) => {
        if (isGoogleSignInSuccess(res)) {
          return {
            token: res.data.access_token,
            ssoUserId: res.data.user_id,
            isExistingUser: res.data.isExistingUser,
          };
        }

        return Promise.reject(res);
      })
      .catch((error) => {
        console.error(
          `Unable to log in with Google:`,
          isError(error) ? error.message : JSON.stringify(error),
        );
        const errorMessage = isGoogleSignInError(error)
          ? error.errors.google[0]
          : 'Unable to log in with Google.';

        return Promise.reject(errorMessage);
      });
  } else {
    return fetch(`${accessToken}`, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'X-PRODUCT-REFERER': product,
      },
    })
      .then((res) => res.json())
      .then(GoogleSignInResponse.parse)
      .then((res) => {
        if (isGoogleSignInSuccess(res)) {
          return {
            token: res.data.access_token,
            ssoUserId: res.data.user_id,
            isExistingUser: res.data.isExistingUser,
          };
        }

        return Promise.reject(res);
      })
      .catch((error) => {
        console.error(
          `Unable to log in with Google with token URL:`,
          isError(error) ? error.message : JSON.stringify(error),
        );
        const errorMessage = isGoogleSignInError(error)
          ? error.errors.google[0]
          : 'Unable to log in with Google.';

        return Promise.reject(errorMessage);
      });
  }
};

type GoogleSignInSuccess = z.infer<typeof GoogleSignInSuccess>;
const GoogleSignInSuccess = z.object({
  data: z.object({
    access_token: z.string(),
    isExistingUser: z.string(),
    user_id: z.number(),
  }),
});
function isGoogleSignInSuccess(
  res: GoogleSignInResponse,
): res is GoogleSignInSuccess {
  return GoogleSignInSuccess.safeParse(res).success;
}

type GoogleSignInError = z.infer<typeof GoogleSignInError>;
const GoogleSignInError = z.object({
  errors: z.object({ google: z.array(z.string()).min(1) }),
});
function isGoogleSignInError(error: unknown): error is GoogleSignInError {
  return GoogleSignInError.safeParse(error).success;
}

type GoogleSignInResponse = z.infer<typeof GoogleSignInResponse>;
const GoogleSignInResponse = z.union([GoogleSignInSuccess, GoogleSignInError]);
